import React, { useState, useEffect } from 'react';
import { X, Zap, Code, Eye, Play, Loader, AlertCircle, CheckCircle, Copy, Download, Share2 } from 'lucide-react';
import { Agent } from '../lib/supabase';
import { useAgentInteractions } from '../hooks/useAgentInteractions';

interface AgentUIModal {
  agent: Agent;
  isOpen: boolean;
  onClose: () => void;
}

interface CodeAnalysis {
  id: string;
  agent_id: string;
  is_code_based: boolean;
  code_type: string;
  programming_language: string;
  main_functionality: string;
  ui_feasible: boolean;
  ui_complexity: string;
  analysis_summary: string;
}

interface UIGeneration {
  id: string;
  agent_id: string;
  ui_type: string;
  ui_components: any[];
  ui_code: string;
  css_styles: string;
  javascript_code: string;
  status: string;
}

export function AgentUIModal({ agent, isOpen, onClose }: AgentUIModal) {
  const [step, setStep] = useState<'analyzing' | 'generating' | 'preview' | 'error'>('analyzing');
  const [codeAnalysis, setCodeAnalysis] = useState<CodeAnalysis | null>(null);
  const [uiGeneration, setUIGeneration] = useState<UIGeneration | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const { trackInteraction } = useAgentInteractions();

  useEffect(() => {
    if (isOpen && agent.id) {
      analyzeCode();
    }
  }, [isOpen, agent.id]);

  const analyzeCode = async () => {
    setLoading(true);
    setError(null);
    setStep('analyzing');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/code-analyzer`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            agent_id: agent.id,
            force_reanalyze: false
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Code analysis failed: ${response.status}\n${errorText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setCodeAnalysis(data.analysis);
        
        if (data.analysis.ui_feasible) {
          generateUI(data.analysis.id);
        } else {
          setStep('error');
          setError('This agent is not suitable for UI generation. It may not be code-based or lacks the necessary structure for creating an interactive interface.');
        }
      } else {
        throw new Error(data.error || 'Code analysis failed');
      }
    } catch (error) {
      console.error('Code analysis error:', error);
      setError(error instanceof Error ? error.message : 'Code analysis failed');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const generateUI = async (codeAnalysisId: string) => {
    setStep('generating');
    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ui-generator`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            agent_id: agent.id,
            code_analysis_id: codeAnalysisId
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`UI generation failed: ${response.status}\n${errorText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setUIGeneration(data.ui_generation);
        setStep('preview');
        trackInteraction(agent.id, 'use_ui');
      } else {
        throw new Error(data.error || 'UI generation failed');
      }
    } catch (error) {
      console.error('UI generation error:', error);
      setError(error instanceof Error ? error.message : 'UI generation failed');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, item: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(item);
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const downloadUI = () => {
    if (!uiGeneration) return;
    
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${agent.name} UI</title>
  <style>
    ${uiGeneration.css_styles}
  </style>
</head>
<body>
  ${uiGeneration.ui_code}
  <script>
    ${uiGeneration.javascript_code}
  </script>
</body>
</html>
    `;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${agent.name.toLowerCase().replace(/\s+/g, '-')}-ui.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderAnalyzingStep = () => (
    <div className="text-center py-12">
      <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyzing Agent Code</h3>
      <p className="text-gray-600">
        Examining the agent's code structure to determine if a UI can be generated...
      </p>
    </div>
  );

  const renderGeneratingStep = () => (
    <div className="text-center py-12">
      <Zap className="w-12 h-12 text-purple-600 animate-pulse mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Interactive UI</h3>
      <p className="text-gray-600 mb-4">
        Creating a custom interface for {agent.name}...
      </p>
      {codeAnalysis && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left max-w-md mx-auto">
          <h4 className="font-medium text-blue-900 mb-2">Analysis Results:</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Type: {codeAnalysis.code_type}</li>
            <li>• Language: {codeAnalysis.programming_language}</li>
            <li>• Functionality: {codeAnalysis.main_functionality}</li>
            <li>• UI Complexity: {codeAnalysis.ui_complexity}</li>
          </ul>
        </div>
      )}
    </div>
  );

  const renderPreviewStep = () => {
    if (!uiGeneration) return null;

    return (
      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">UI Generated Successfully!</span>
          </div>
          <p className="text-green-600 text-sm mt-1">
            Interactive {uiGeneration.ui_type} interface created for {agent.name}
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
            <h4 className="font-medium text-gray-900">Live Preview</h4>
            <div className="flex gap-2">
              <button 
                onClick={() => copyToClipboard(uiGeneration.ui_code, 'html')}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copy HTML"
              >
                {copiedItem === 'html' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
              <button 
                onClick={() => window.open(`/agent-ui/${uiGeneration.id}`, '_blank')}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Open in new tab"
              >
                <Eye className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="p-4 bg-white">
            <style dangerouslySetInnerHTML={{ __html: uiGeneration.css_styles }} />
            <div 
              dangerouslySetInnerHTML={{ __html: uiGeneration.ui_code }}
            />
            <script dangerouslySetInnerHTML={{ __html: uiGeneration.javascript_code }} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">UI Components</h4>
            <div className="space-y-2">
              {uiGeneration.ui_components.map((component, index) => (
                <div key={index} className="text-sm text-gray-600">
                  <span className="font-medium">{component.type}:</span> {component.label || component.id}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Interface Details</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <div><span className="font-medium">Type:</span> {uiGeneration.ui_type}</div>
              <div><span className="font-medium">Components:</span> {uiGeneration.ui_components.length}</div>
              <div><span className="font-medium">Status:</span> {uiGeneration.status}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => window.open(`/agent-ui/${uiGeneration.id}`, '_blank')}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Play className="w-4 h-4" />
            Open in New Tab
          </button>
          <button
            onClick={() => copyToClipboard(uiGeneration.ui_code, 'ui')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            {copiedItem === 'ui' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            Copy UI Code
          </button>
          <button
            onClick={downloadUI}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download HTML
          </button>
          <button
            onClick={() => copyToClipboard(`<iframe src="${window.location.origin}/agent-ui/${uiGeneration.id}" width="100%" height="500" frameborder="0"></iframe>`, 'embed')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            {copiedItem === 'embed' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Share2 className="w-4 h-4" />}
            Copy Embed Code
          </button>
        </div>
      </div>
    );
  };

  const renderErrorStep = () => (
    <div className="text-center py-12">
      <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">UI Generation Not Available</h3>
      <p className="text-gray-600 mb-6">{error}</p>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left max-w-md mx-auto">
        <h4 className="font-medium text-yellow-800 mb-2">Requirements for UI Generation:</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• Agent must be code-based (GitHub repository)</li>
          <li>• Code should have clear functions or API endpoints</li>
          <li>• Repository must be publicly accessible</li>
          <li>• Code structure should be analyzable</li>
        </ul>
      </div>
      <button
        onClick={analyzeCode}
        className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Retry Analysis
      </button>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-purple-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">UI Generator</h2>
              <p className="text-sm text-gray-600">{agent.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'analyzing' && renderAnalyzingStep()}
          {step === 'generating' && renderGeneratingStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'error' && renderErrorStep()}
        </div>
        
        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}